'use client'

import Loading from '@app/clinical-trials/loading'
import { trackEvent, trackPageview } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { ITEMS_PER_SUBPAGE } from '@modules/trials/search/const'
import type { AlgoliaSearchResultAndUiState } from '@modules/trials/search/getDataFromAlgolia'
import getUniqueTrialsFromTrialLocations from '@modules/trials/search/helpers/getUniqueTrialsFromTrialLocations'
import { usePathname, useSearchParams } from 'next/navigation'
import { Suspense, use, useEffect } from 'react'
import ListingDetails from '../ListingDetails'
import QuickFilters from '../QuickFilters'
import ReferralSection from '../ReferralSection.client'
import TrialCard from '../TrialCard'
import TrustUsSection from '../TrustUsSection'
import NoResultsSection from './NoResultsSection.client'
import SearchResultsPagination from './SearchResultsPagination.client'

export default function ListingResults({
  dataFromAlgoliaPromise,
  desktopFilterDropdown,
  patientStories,
}: {
  dataFromAlgoliaPromise: Promise<AlgoliaSearchResultAndUiState>
  desktopFilterDropdown: React.ReactNode
  patientStories?: React.ReactNode
}) {
  const searchParams = useSearchParams()
  const pathname = usePathname() ?? ''

  const { algoliaResponse, searchLanderPageData } = use(dataFromAlgoliaPromise)
  const { activeConditionLabel, activeLocationLabel, activeTreatmentLabel } =
    searchLanderPageData

  const uniqueTrials = getUniqueTrialsFromTrialLocations(algoliaResponse.hits)

  const pageParam = searchParams?.get('page')
  const currentSubPage = pageParam ? parseInt(pageParam) ?? 1 : 1

  const totalSubPagesInAlgoliaPage = uniqueTrials
    ? Math.ceil(uniqueTrials.length / ITEMS_PER_SUBPAGE)
    : 0

  const startIndex = (currentSubPage - 1) * ITEMS_PER_SUBPAGE
  const endIndex = startIndex + ITEMS_PER_SUBPAGE
  const uniqueTrialsToDisplay = uniqueTrials?.slice(startIndex, endIndex)

  const firstTrialChunk = uniqueTrialsToDisplay?.slice(0, 20) || []
  const remainingTrialChunk = uniqueTrialsToDisplay?.slice(20) || []

  // Track the number of results returned from the search so product can understand CTR rate of various trial cards
  useEffect(() => {
    if (uniqueTrials.length > 0) {
      trackEvent(TrackingEvents.SEARCH_LANDER_RESULTS_VIEWED, {
        val: uniqueTrialsToDisplay.map((trial) => trial.nctId).join(','),
      })
    } else {
      trackEvent(TrackingEvents.SEARCH_LANDER_NO_RESULTS_HIT)
    }
    trackPageview(pathname, searchParams, {})
  }, [uniqueTrialsToDisplay])

  return (
    <div className='grid w-screen grid-cols-1 lg:min-h-screen lg:grid-cols-[320px,905px,auto] lg:gap-6 lg:pl-6'>
      <div className='hidden lg:block'>{desktopFilterDropdown}</div>
      <div>
        <Suspense fallback={<Loading />}>
          <ListingDetails
            activeConditionLabel={activeConditionLabel}
            activeLocationLabel={activeLocationLabel}
            activeTreatmentLabel={activeTreatmentLabel}
            trialCount={uniqueTrials?.length ?? 0}
          />
        </Suspense>
        <QuickFilters />
        {/* if results are empty, show a message */}
        {uniqueTrialsToDisplay?.length === 0 && <NoResultsSection />}

        <div className='mt-4 flex flex-col gap-6 lg:mt-6'>
          {firstTrialChunk.map((trial) => {
            return (
              <div key={trial.objectID}>
                <TrialCard {...trial} />
              </div>
            )
          })}

          {uniqueTrialsToDisplay &&
            uniqueTrialsToDisplay.length > 0 &&
            patientStories && <div>{patientStories}</div>}

          {remainingTrialChunk.map((trial) => {
            return (
              <div key={trial.objectID}>
                <TrialCard {...trial} />
              </div>
            )
          })}
        </div>
        {totalSubPagesInAlgoliaPage > 1 && (
          <SearchResultsPagination
            className='my-6'
            currentPage={currentSubPage}
            totalPages={totalSubPagesInAlgoliaPage}
          />
        )}
        <ReferralSection pathname={pathname} />
      </div>
      <div className='mt-40 hidden pt-2 2xl:block'>
        <TrustUsSection />
      </div>
    </div>
  )
}
